import { Link } from 'react-router-dom';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import {
    faChevronDown,
    faFileExport,
    faFilePdf,
    faPlus,
    faSearch,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as ExportBidCardsDialog from 'components/pages/campaign-settings/campaign-auction/bidders-index/ExportBidCardsDialog';
import * as ExportBiddersDialog from 'components/pages/campaign-settings/campaign-auction/bidders-index/ExportBiddersDialog';
import { useBiddersIndex } from 'components/pages/campaign-settings/campaign-auction/bidders-index/useBiddersIndex';
import * as Table from 'components/table';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useFlags } from 'launchdarkly-react-client-sdk';

const BiddersIndex = () => {
    const { table, search, setSearch, isPending, searchTerm } = useBiddersIndex();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const { auctionsCreateBidder = false } = useFlags();
    const hasNoBidders = table.getRowModel().rows.length === 0;

    return (
        <div className={'flex flex-col gap-y-4'}>
            <h3
                data-intercom-target={IntercomProductTourTargets.AuctionBiddersTitle}
                className={'text-2xl font-medium'}
            >
                Bidders
            </h3>
            <div className={'flex items-center justify-between'}>
                <Input.Root className={'w-80'}>
                    <FontAwesomeIcon icon={faSearch} className={'text-gray-300'} />
                    <Input.Form
                        placeholder={'Search'}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Input.Root>
                <div className={'flex gap-x-2'}>
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                            <Button variant="outline" color="neutral">
                                Actions
                                <FontAwesomeIcon icon={faChevronDown} />
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content sideOffset={4} align={'end'}>
                            <ExportBiddersDialog.Root>
                                <ExportBiddersDialog.Trigger asChild={true}>
                                    <DropdownMenu.Item
                                        disabled={hasNoBidders}
                                        onSelect={e => e.preventDefault()}
                                    >
                                        <div className={'flex items-center gap-x-2'}>
                                            <FontAwesomeIcon icon={faFileExport} />
                                            Export bidders
                                        </div>
                                    </DropdownMenu.Item>
                                </ExportBiddersDialog.Trigger>
                            </ExportBiddersDialog.Root>

                            <ExportBidCardsDialog.Root>
                                <ExportBidCardsDialog.Trigger asChild={true}>
                                    <DropdownMenu.Item
                                        disabled={hasNoBidders}
                                        onSelect={e => e.preventDefault()}
                                    >
                                        <div className={'flex items-center gap-x-2'}>
                                            <FontAwesomeIcon icon={faFilePdf} />
                                            Export auction bid cards
                                        </div>
                                    </DropdownMenu.Item>
                                </ExportBidCardsDialog.Trigger>
                            </ExportBidCardsDialog.Root>
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                    {auctionsCreateBidder ? (
                        <Link
                            to={`/accounts/${accountId}/campaigns/${campaign?.id}/auction/bidders/create`}
                        >
                            <Button variant="solid">
                                <FontAwesomeIcon icon={faPlus} /> Add bidder
                            </Button>
                        </Link>
                    ) : null}
                </div>
            </div>
            <Table.Root
                isLoading={isPending}
                table={table}
                searchTerm={searchTerm}
                variant={'bordered'}
            >
                <Box className={'overflow-x-auto'}>
                    <Table.Content>
                        <Table.Header />
                        <Table.Body />
                    </Table.Content>
                    <Table.Empty variant={'secondary'}>
                        <Table.EmptyIcon />
                        <Table.EmptyTitle>Your auction has no registered bidders</Table.EmptyTitle>
                        <Table.EmptyDescription>
                            Ask supporters to register to your auction.
                        </Table.EmptyDescription>
                    </Table.Empty>
                </Box>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </Table.Root>
        </div>
    );
};

export { BiddersIndex };
